import React from 'react';
import { DraftNumberInput } from '../../../../shared/components/DraftNumberInput';

/**
 * Slide-over drawer for the NDT rates, prefilled from the active config and
 * overridable per proposal. Same pattern as the Material Traceability and
 * PipeFitter drawers: the per-inspection cost-model inputs plus the shared
 * onboarding (NRE) rates and format difficulty that drive the per-vendor
 * onboarding NRE.
 */

const PERCENT = 100;

export const NDT_RATE_FALLBACKS: Record<string, number> = {
  reportsPerInspection: 1.0,
  autoExtractRate: 0.98,
  minutesPerException: 4,
  hourlyRate: 100,
  computeCostPerDoc: 0.04,
  aiComputeCostPerDoc: 0.2,
  storageCostPerInspection: 0.05,
  qaOverheadPct: 0.1,
  marginTarget: 0.75,
  floorPerInspection: 0.5,
  ceilingPerInspection: 8,
  moduleFloor: 20000,
};

const NDT_NRE_FALLBACKS: Record<string, number> = {
  setupHours: 8,
  perUnitHours: 2,
  validationOverhead: 0.25,
  engineerRate: 100,
  marginTarget: 0.75,
};
const DIFFICULTY_FALLBACKS: Record<string, number> = {
  api: 0.5,
  spreadsheet: 1,
  scannedPdf: 2,
  handwritten: 3,
};

interface RateField {
  key: string;
  label: string;
  percent?: boolean;
  step?: number;
  tooltip: string;
  nre?: boolean;
}

interface Group {
  title: string;
  fields: RateField[];
}

const GROUPS: Group[] = [
  {
    title: 'Per-inspection cost model',
    fields: [
      { key: 'reportsPerInspection', label: 'Reports / insp', step: 1, tooltip: 'Report documents per inspection. Each document is extracted; the share that fails auto-extraction needs manual exception labour. Drives the per-inspection cost.' },
      { key: 'autoExtractRate', label: 'Auto-extract %', percent: true, step: 1, tooltip: 'Share of documents extracted automatically. The rest need manual exception labour.' },
      { key: 'minutesPerException', label: 'Min / exception', step: 1, tooltip: 'Minutes to fix one exception document by hand.' },
      { key: 'hourlyRate', label: 'Labour $/hr', step: 1, tooltip: 'Exception-handling labour hourly rate.' },
      { key: 'computeCostPerDoc', label: 'Compute $/doc', step: 0.01, tooltip: 'Extraction compute cost per document.' },
      { key: 'aiComputeCostPerDoc', label: 'AI compute $/doc', step: 0.01, tooltip: 'Added compute cost per document on the NDT + AI tier.' },
      { key: 'storageCostPerInspection', label: 'Storage $/insp', step: 0.01, tooltip: 'Storage cost per inspection record.' },
      { key: 'qaOverheadPct', label: 'QA overhead %', percent: true, step: 1, tooltip: 'QA overhead as a share of the exception labour.' },
      { key: 'marginTarget', label: 'Margin %', percent: true, step: 1, tooltip: 'Target margin: price = cost / (1 - margin).' },
    ],
  },
  {
    title: 'Per-record bounds',
    fields: [
      { key: 'floorPerInspection', label: 'Floor $/insp', step: 0.1, tooltip: 'Minimum price per inspection record.' },
      { key: 'ceilingPerInspection', label: 'Ceiling $/insp', step: 0.5, tooltip: 'Maximum price per inspection record. 0 means no ceiling.' },
    ],
  },
  {
    title: 'Vendor onboarding (NRE) rates',
    fields: [
      { key: 'setupHours', label: 'Setup hours', nre: true, tooltip: 'Base engineering hours to onboard a vendor.' },
      { key: 'perUnitHours', label: 'Hours / type', nre: true, tooltip: 'Build hours per data type, scaled by format difficulty.' },
      { key: 'validationOverhead', label: 'Validation overhead %', nre: true, percent: true, step: 1, tooltip: 'Validation overhead added to build hours.' },
      { key: 'engineerRate', label: 'Engineer $/hr', nre: true, tooltip: 'Onboarding engineer hourly rate.' },
      { key: 'marginTarget', label: 'NRE margin %', nre: true, percent: true, step: 1, tooltip: 'Target margin on vendor onboarding.' },
    ],
  },
  {
    title: 'Vendor format difficulty',
    fields: [
      { key: 'difficultyMultipliers.api', label: 'API', nre: true, step: 0.1, tooltip: 'Onboarding difficulty for API / native-digital vendors.' },
      { key: 'difficultyMultipliers.spreadsheet', label: 'Spreadsheet', nre: true, step: 0.1, tooltip: 'Onboarding difficulty for spreadsheet vendors.' },
      { key: 'difficultyMultipliers.scannedPdf', label: 'Scanned PDF', nre: true, step: 0.1, tooltip: 'Onboarding difficulty for scanned-PDF vendors.' },
      { key: 'difficultyMultipliers.handwritten', label: 'Handwritten', nre: true, step: 0.1, tooltip: 'Onboarding difficulty for handwritten vendors.' },
    ],
  },
  {
    title: 'Floor',
    fields: [
      { key: 'moduleFloor', label: 'Module floor', tooltip: 'Minimum the module charges if the summed lines fall below it.' },
    ],
  },
  {
    title: 'Discount',
    fields: [
      { key: 'durationDiscountPct', label: 'Duration discount %', tooltip: 'Overrides the duration-band discount for this proposal. Applies to the per-inspection charge.' },
    ],
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
  config?: Record<string, number>;
  nreConfig?: Record<string, unknown>;
  overrides: Record<string, number>;
  onOverrideChange: (key: string, value: number | undefined) => void;
  onResetAll: () => void;
  discountDefault?: number;
}

const readNested = (source: Record<string, unknown> | undefined, key: string): number | undefined => {
  const value = key
    .split('.')
    .reduce<unknown>((acc, part) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[part] : undefined), source);
  return typeof value === 'number' ? value : undefined;
};

export const NDTRatesDrawer: React.FC<Props> = ({
  open,
  onClose,
  config,
  nreConfig,
  overrides,
  onOverrideChange,
  onResetAll,
  discountDefault = 0,
}) => {
  if (!open) return null;

  const overrideKeyOf = (field: RateField): string => (field.nre ? `nre.${field.key}` : field.key);

  const defaultValue = (field: RateField): number => {
    if (field.key === 'durationDiscountPct') return discountDefault;
    if (field.nre) {
      const lastPart = field.key.split('.').pop() ?? field.key;
      return readNested(nreConfig, field.key) ?? NDT_NRE_FALLBACKS[field.key] ?? DIFFICULTY_FALLBACKS[lastPart] ?? 0;
    }
    return config?.[field.key] ?? NDT_RATE_FALLBACKS[field.key] ?? 0;
  };

  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label="NDT rates">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-[#E2E5EA] px-5 py-4">
          <div>
            <h3 className="text-base font-semibold font-heading text-blueprint-blue">NDT cost assumptions</h3>
            <p className="mt-0.5 text-xs text-heritage-grey">
              From the active configuration. Edit any value to override it for this proposal only.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-heritage-grey hover:text-blueprint-blue" aria-label="Close">
            &times;
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-4">
          {GROUPS.map((group) => (
            <div key={group.title}>
              <h4 className="text-sm font-medium text-blueprint-blue">{group.title}</h4>
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-4">
                {group.fields.map((field) => {
                  const key = overrideKeyOf(field);
                  const override = overrides[key];
                  const isOverridden = override !== undefined;
                  const def = defaultValue(field);
                  const native = isOverridden ? override : def;
                  const shown = field.percent ? native * PERCENT : native;
                  return (
                    <div key={key}>
                      <DraftNumberInput
                        id={`ndt-rate-${key}`}
                        label={field.label}
                        tooltip={field.tooltip}
                        step={field.step ?? 'any'}
                        value={shown}
                        className={isOverridden ? 'border-primary' : ''}
                        onValueChange={(raw) => {
                          if (raw === '') {
                            onOverrideChange(key, undefined);
                            return;
                          }
                          const n = Number(raw);
                          if (Number.isNaN(n)) return;
                          onOverrideChange(key, field.percent ? n / PERCENT : n);
                        }}
                      />
                      <div className="mt-0.5 flex items-center justify-between text-[11px] text-heritage-grey">
                        <span>default {field.percent ? `${def * PERCENT}%` : def}</span>
                        {isOverridden && (
                          <button type="button" className="text-primary hover:underline" onClick={() => onOverrideChange(key, undefined)}>
                            reset
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-[#E2E5EA] px-5 py-3">
          <button type="button" onClick={onResetAll} className="text-sm text-text-grey hover:text-blueprint-blue">
            Reset all to defaults
          </button>
          <button type="button" onClick={onClose} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
